/* eslint-disable prefer-destructuring */
/* eslint-disable complexity */
import { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { LoaderCircle } from 'lucide-react';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetFormulasQueryKey,
  glacierPolicyApiGetDentistQueryKey,
  glacierPolicyApiGetSpecialFormsMutation,
  glacierPolicyFormsApiPolicyLevelAttachedEndorsementsQueryKey,
  glacierPolicyFormsApiGetAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetDentistAttachedFormsQueryKey,
  glacierPolicyFormsApiGetDentistAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetLocationAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetLocationAttachedFormsQueryKey,
  glacierPolicyFormsApiGetEntityAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetEntityAttachedFormsQueryKey,
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiListDocumentsQueryKey,
  glacierPolicyApiGetPolicyOptions,
  glacierPolicyApiRatePolicyMutation,
  glacierPolicyApiGetLatestTransactionLatestAttemptQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { useParams, Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Policy, TransactionSummaryData } from '@/lib/heyapi/types.gen';
import {
  PolicyTypeEnum,
  TransactionStatus,
  TransactionType,
} from '@/lib/heyapi/types.gen';

import { toast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { PermissionsContext } from '@/lib/layout/PermissionContext';
import { userRoleEnum, permissionGroupEnum } from '@/lib/layout/types';

import { EndorsementForm } from '@/lib/layout/forms/endorsement';
import { ERPForm } from '@/lib/layout/forms/ERPForm';
import { ReinstateForm } from '@/lib/layout/forms/reinstate';
import { formatErrorMessage } from '../utils';
import { ErrorDialog } from './DropdownComponents/ErrorDialog';
import { DebugTransaction } from './DropdownComponents/DebugTransaction';
import { Transfer } from './DropdownComponents/Transfer';
import { Decline } from './DropdownComponents/Decline';
import { Discard } from './DropdownComponents/Discard';
import { Reopen } from './DropdownComponents/Reopen';
import ReopenUnlockedAccountDialog from './DropdownComponents/ReopenUnlockedAccountDialog';
import LockPolicyDialog from './DropdownComponents/LockPolicyDialog';
import IssuePolicyDialog from './DropdownComponents/IssuePolicyDialog';
import BypassValidationDialog from './DropdownComponents/BypassValidationDialog';
import type { Message } from './StandardMessage';
import { Refer } from '@/lib/layout/Header/components/DropdownComponents/Refer';
import { useCurrentUserData } from '@/lib/utils/hooks';
import { CancelPolicyDialog } from './DropdownComponents/CancelPolicy';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';

export const DropdownMenuButton = ({
  policy,
  latestTransactionSummary,
  setMessage,
}: {
  policy: Policy | undefined;
  latestTransactionSummary?: TransactionSummaryData;
  setMessage: (message: Message | undefined) => void;
}) => {
  const queryClient = useQueryClient();
  const { setErrors } = useContext(RatingErrorsContext);
  const navigate = useNavigate();
  const {
    editMode,
    role,
    agentAuthority,
    refetchAgentAuthority,
    hasPermission,
  } = useContext(PermissionsContext);
  const { id, dentistId, locationId, entityId } = useParams();
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [bypassing, setBypassing] = useState(false);
  const [forming, setForming] = useState(false);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [dialogContent, setDialogContent] = useState<
    | 'RATING'
    | 'TRANSFER'
    | 'REFER'
    | 'DISCARD'
    | 'DECLINE'
    | 'NOT_TAKEN'
    | 'REOPEN'
    | undefined
  >(undefined);

  const { currentUser } = useCurrentUserData();
  const userAgencies = currentUser?.data?.agentData || [];
  const policyAgencyId = policy?.details.agency;
  const canRefer = userAgencies.some(
    (agency) => agency.canRefer && agency.agencyId === policyAgencyId
  );

  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [cancelPolicyDialogOpen, setCancelPolicyDialogOpen] = useState(false);
  const [lockPolicyDialogOpen, setLockPolicyDialogOpen] = useState(false);
  const [issuePolicyDialogOpen, setIssuePolicyDialogOpen] = useState(false);
  const [bypassValidationDialogOpen, setBypassValidationDialogOpen] =
    useState(false);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
    };
  }, []);

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const warehouseKey = glacierPolicyApiListDocumentsQueryKey({
    path: { account_id: id! },
  });

  const formulasQK = glacierPolicyApiGetFormulasQueryKey({
    path: {
      account_id: id!,
    },
  });

  const permissionsQK = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: {
      account_id: id!,
    },
  });

  const dentistQK = glacierPolicyApiGetDentistQueryKey({
    path: {
      account_id: id!,
      dentist_id: dentistId || '',
    },
  });

  const policyLevelAttachedFQK =
    glacierPolicyFormsApiPolicyLevelAttachedEndorsementsQueryKey({
      path: {
        account_id: id!,
      },
    });

  const policyLevelAvailableFQK =
    glacierPolicyFormsApiGetAvailableEndorsementsQueryKey({
      path: {
        account_id: id!,
      },
    });

  const getDentistAttachedFQK =
    glacierPolicyFormsApiGetDentistAttachedFormsQueryKey({
      path: {
        account_id: id!,
        dentist_id: dentistId!,
      },
    });

  const getDentistAvailableFQK =
    glacierPolicyFormsApiGetDentistAvailableEndorsementsQueryKey({
      path: {
        dentist_id: dentistId!,
        account_id: id!,
      },
    });

  const getEntityAttachedFQK =
    glacierPolicyFormsApiGetEntityAttachedFormsQueryKey({
      path: {
        entity_id: entityId!,
        account_id: id!,
      },
    });

  const getEntityAvailableFQK =
    glacierPolicyFormsApiGetEntityAvailableEndorsementsQueryKey({
      path: {
        entity_id: entityId!,
        account_id: id!,
      },
    });

  const locationAttachedFQK =
    glacierPolicyFormsApiGetLocationAttachedFormsQueryKey({
      path: {
        location_id: locationId!,
        account_id: id!,
      },
    });

  const locationAvailableFQK =
    glacierPolicyFormsApiGetLocationAvailableEndorsementsQueryKey({
      path: {
        location_id: locationId!,
        account_id: id!,
      },
    });

  const latestRatingAttemptFQK =
    glacierPolicyApiGetLatestTransactionLatestAttemptQueryKey({
      path: {
        account_id: id!,
      },
    });

  // Not in use now. Quote Proposal also generates the policy specimen
  const _issuePolicySpecimen = useMutation({
    ...glacierPolicyApiGetSpecialFormsMutation(),
    onSuccess() {
      setIssuing(false);

      queryClient.refetchQueries({ queryKey: warehouseKey });
      setMessage({
        content: 'Policy Specimen generated. Check Document Warehouse',
        level: 'INFO',
      });
    },
    onError() {
      setIssuing(false);
      setForming(false);
      setMessage({
        content: 'Error generating the Policy Specimen',
        level: 'ERROR',
      });
    },
  });

  const quoteForm = useMutation({
    ...glacierPolicyApiGetSpecialFormsMutation(),
    onSuccess() {
      setForming(false);
      setIssuing(false);
      queryClient.refetchQueries({ queryKey: warehouseKey });

      // Clear any existing timeout and show banner for 10 seconds
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
      bannerTimeoutRef.current = setTimeout(() => {
        setMessage(undefined);
      }, 6000);
    },
    onError(error) {
      setForming(false);
      setIssuing(false);

      if (error.status === 422) {
        setErrors(error.errors || null);

        toast({
          title: 'Error',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <p className="text-white">
                Some required data is missing, please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('ratingError', 'true');

        navigate('?' + searchParams.toString());
      }

      setMessage({
        content: 'Error generating the Quote Proposal',
        level: 'ERROR',
      });
    },
  });

  useEffect(() => {
    if (policy?.bypassValidators) {
      setMessage({
        content: 'Data Validation is disabled for the current transaction.',
        level: 'INFO',
      });
    }
  }, [policy?.bypassValidators]);

  const rate = useMutation({
    ...glacierPolicyApiRatePolicyMutation(),
    onSuccess(data, variables) {
      queryClient.setQueryData(
        glacierPolicyApiGetPolicyOptions({ ...variables }).queryKey,
        data
      );
      queryClient.refetchQueries({ queryKey: policyQK });
      queryClient.refetchQueries({ queryKey: formulasQK });
      queryClient.refetchQueries({ queryKey: dentistQK });
      queryClient.refetchQueries({
        queryKey: [{ _id: 'glacierPolicyApiGetObjectDetails' }],
        exact: false,
      });

      queryClient.refetchQueries({ queryKey: policyLevelAttachedFQK });
      queryClient.refetchQueries({ queryKey: policyLevelAvailableFQK });
      queryClient.refetchQueries({ queryKey: getDentistAttachedFQK });
      queryClient.refetchQueries({ queryKey: getDentistAvailableFQK });
      queryClient.refetchQueries({ queryKey: getEntityAttachedFQK });
      queryClient.refetchQueries({ queryKey: getEntityAvailableFQK });
      queryClient.refetchQueries({ queryKey: locationAttachedFQK });
      queryClient.refetchQueries({ queryKey: locationAvailableFQK });
      queryClient.refetchQueries({ queryKey: latestRatingAttemptFQK });
      setLoading(false);
      setMessage({
        content: 'Rated successfully.',
        level: 'SUCCESS',
      });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Account Rated</code>
          </pre>
        ),
      });

      // if current transaction type is Endorsement, refresh page
      if (
        policy?.latestTransaction.transactionType ===
        TransactionType.POLICY_ENDORSEMENT
      ) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    },
    onError(error: any) {
      setLoading(false);
      let messageContent = 'Error while rating the policy.';
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey: formulasQK });

      if (error.status_message) {
        messageContent = `Unable to Rate: ${error.status_message}`;
      }
      setMessage({
        content: messageContent,
        level: 'ERROR',
      });
      if (error.status === 422) {
        setErrors(error.errors || null);

        toast({
          title: 'Error',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <p className="text-white">
                Some required data is missing, please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('ratingError', 'true');

        navigate('?' + searchParams.toString());
      } else {
        let title = 'Missing required fields for policy issuance';

        if (error.errors && Array.isArray(error.errors)) {
          title = 'Missing required fields for rating';
        } else if (error.detail?.includes('Error(')) {
          title = error.detail.split(':')[0];
        }

        const content = formatErrorMessage(error);

        setMessage({
          content: (
            <ErrorDialog title={title} onClose={() => setMessage(undefined)}>
              {content}
            </ErrorDialog>
          ),
          level: 'ERROR',
        });
      }
    },
  });

  const policyBoundButTransactionInProgress = useMemo(() => {
    // Used to determine if the policy is bound but not locked
    // Will remove once we lock all accounts and launch midterms
    if (
      policy &&
      policy?.latestTransaction.status === TransactionStatus.IN_PROGRESS &&
      policy?.boundPolicyNumber &&
      policy?.latestTransaction.transactionType === TransactionType.POLICY_BOUND
    ) {
      return true;
    }
    return false;
  }, [policy?.latestTransaction.status, policy?.boundPolicyNumber]);

  const declinable = useMemo(() => {
    return policy?.latestTransaction.status === TransactionStatus.IN_PROGRESS;
  }, [policy?.latestTransaction]);

  const discardable = useMemo(() => {
    const emptyBoundPolicyNumber = !policy?.boundPolicyNumber;

    return (
      emptyBoundPolicyNumber &&
      policy?.latestTransaction.status !== TransactionStatus.DISCARDED
    );
  }, [policy, role]);

  const reopenable = useMemo(() => {
    return (
      policy?.latestTransaction.status === TransactionStatus.DECLINED ||
      policy?.latestTransaction.status === TransactionStatus.NOT_TAKEN
    );
  }, [policy?.latestTransaction, role]);

  // IF (
  //     current_transaction.transaction_status == `ACTIVE`
  //     AND today is < policy.expiration_date
  // ):
  // THEN Show the Midterms menu
  const ratingModalCSS =
    'w-full max-w-7xl overflow-auto [&>button]:right-2 [&>button]:top-2';
  const modalCSS = 'w-full overflow-auto [&>button]:right-8 [&>button]:top-8';
  const erpFeatureFlag = import.meta.env.VITE_ENABLE_ERP === 'true';
  const reinstateFeatureFlag = import.meta.env.VITE_ENABLE_REINSTATE === 'true';

  // Show Midterms if
  // - current transaction is ACTIVE
  // - We have a policy expiration date
  // - And if the Policy is not Cancelled, then today is < policy expiration date
  //     - This is to make sure that Cancelled policies can be reinstated
  //       but Expired policies cannot have midterms
  let showMidterms = false;

  const policyCancelled =
    policy?.latestTransaction.transactionType ===
    TransactionType.POLICY_CANCELLED;

  const policyReinstated =
    policy?.latestTransaction.transactionType ===
    TransactionType.POLICY_REINSTATED;

  if (
    policy?.latestTransaction.status === TransactionStatus.ACTIVE &&
    policy?.details?.policyExpirationDate
  ) {
    // - If the Policy is not Cancelled, then validate today is < policy expiration date
    //     - This is to make sure that Cancelled policies can be reinstated
    //       but Expired policies cannot have midterms
    // - If the Policy is Reinstated, allow midterms
    if (policyCancelled || policyReinstated) {
      showMidterms = true;
    } else if (new Date() < new Date(policy?.details?.policyExpirationDate)) {
      showMidterms = true;
    }
  }

  // Allow endorsement midterm if:
  // - current transaction is not POLICY_CANCELLED
  // - AND IF it the last one was ERP
  //   THEN the effective date of the last transaction is not equal to the policy expiration date
  const allowEndorsementMidterm =
    policy?.latestTransaction.transactionType !==
      TransactionType.POLICY_CANCELLED &&
    !(
      policy?.latestTransaction.transactionType === TransactionType.ERP &&
      policy?.latestTransaction.effectiveDate ===
        policy?.details?.policyExpirationDate
    );

  // Allow cancel if last transaction is not POLICY_CANCELLED
  // OR if the last transaction effective date != policy expiration date
  const allowCancelMidterm =
    policy?.latestTransaction.transactionType !==
      TransactionType.POLICY_CANCELLED ||
    policy?.latestTransaction.effectiveDate !=
      policy?.details?.policyExpirationDate;

  const allowReinstateMidterm =
    policy?.latestTransaction.transactionType ===
    TransactionType.POLICY_CANCELLED;

  const enableERP =
    (erpFeatureFlag &&
      latestTransactionSummary?.availableActions?.includes('start_erp')) ||
    false;

  if (loading) {
    <div className="flex flex-row-reverse items-center gap-4">
      <span className="text-sm">Rating</span>
      <LoaderCircle className="h-10 w-10 animate-spin text-brand-200" />
    </div>;
  }

  const disabledGenerateQuote =
    policy?.canBeQuoted !== true ||
    issuing ||
    forming ||
    !editMode ||
    (role === userRoleEnum.AGENT && agentAuthority?.issueQuote === false);

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              className="h-10 w-10 bg-[none] p-0 hover:bg-brand-200"
              onClick={() => {
                if (role === userRoleEnum.AGENT) {
                  refetchAgentAuthority();
                }
              }}
            >
              <DotsHorizontalIcon className="!hover:bg-[none] h-4 w-4 rounded-none text-[black]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {}} disabled>
              <span className="ml-2">Approve</span>
            </DropdownMenuItem>
            {role !== userRoleEnum.AGENT && declinable && (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('DECLINE')}
              >
                <DropdownMenuItem disabled={!editMode}>
                  <span className="ml-2">Decline</span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}
            {role !== userRoleEnum.AGENT && reopenable && (
              <DialogTrigger asChild onClick={() => setDialogContent('REOPEN')}>
                <DropdownMenuItem>
                  <span className="ml-2">Reopen</span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}

            {discardable && (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('DISCARD')}
              >
                <DropdownMenuItem>
                  <span className="ml-2">Discard</span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}

            {role === userRoleEnum.AGENT && declinable && (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('NOT_TAKEN')}
              >
                <DropdownMenuItem disabled={!editMode}>
                  <span className="ml-2">Not Taken</span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}

            <DialogTrigger asChild onClick={() => setDialogContent('TRANSFER')}>
              <DropdownMenuItem>
                <span className="ml-2">Transfer</span>
              </DropdownMenuItem>
            </DialogTrigger>

            {role === userRoleEnum.AGENT && canRefer && (
              <DialogTrigger asChild onClick={() => setDialogContent('REFER')}>
                <DropdownMenuItem>
                  <span className="ml-2">Refer</span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}

            <DropdownMenuItem
              onClick={() => {
                setForming(true);
                quoteForm.mutate({
                  path: { account_id: policy!.pk! },
                  query: { special_form: 'quote_proposal' },
                });
                setMessage({
                  content:
                    'Quote Proposal and Policy Specimen are being generated. Check Document Warehouse.',
                  level: 'INFO',
                });
              }}
              disabled={disabledGenerateQuote}
            >
              <span className="ml-2">
                {issuing || forming ? 'Generating Quote...' : 'Generate Quote'}
              </span>
            </DropdownMenuItem>

            {policy?.latestTransaction.transactionType ===
              TransactionType.POLICY_BOUND && (
              // Only show if we are on a POLICY_BOUND transaction
              <DropdownMenuItem
                onClick={() => setIssuePolicyDialogOpen(true)}
                disabled={
                  issuing ||
                  !editMode ||
                  (role === userRoleEnum.AGENT &&
                    agentAuthority?.issuePolicy === false)
                }
              >
                <span className="ml-2">
                  {issuing ? 'Issuing Policy...' : 'Issue Policy'}
                </span>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => {
                setLoading(true);

                setMessage({
                  content: 'Application is being rated. Please wait...',
                  level: 'INFO',
                });
                rate.mutate({ path: { account_id: policy!.pk! } });
              }}
              disabled={!editMode}
            >
              <span className="ml-2">Rate</span>
            </DropdownMenuItem>

            {hasPermission(permissionGroupEnum.UNDERWRITING_ADMIN) && (
              // Only show if we are on a POLICY_BOUND transaction or ROLL_OVER policy and user has underwriting admin permission or higher
              <DropdownMenuItem
                onClick={() => setBypassValidationDialogOpen(true)}
                disabled={
                  issuing ||
                  !editMode ||
                  (role === userRoleEnum.AGENT &&
                    agentAuthority?.issuePolicy === false)
                }
              >
                <span className="ml-2">
                  {policy?.bypassValidators
                    ? 'Enable Data Validation'
                    : 'Disable Data Validation'}
                </span>
              </DropdownMenuItem>
            )}

            {role !== userRoleEnum.AGENT && (
              <DialogTrigger
                asChild
                onClick={() => {
                  setDialogContent('RATING');
                  setLoadingDebug(true);
                }}
              >
                <DropdownMenuItem
                  disabled={
                    (role !== userRoleEnum.SYSTEM_ADMIN && editMode) ||
                    loadingDebug
                  }
                >
                  <span className="ml-2 flex items-center gap-4">
                    {loadingDebug ? (
                      <span className="flex items-center">
                        Fetching Previous Rating...
                        <LoaderCircle className="ml-2 h-4 w-4 animate-spin text-brand-200" />
                      </span>
                    ) : (
                      <span>Debug Previous Rating</span>
                    )}
                  </span>
                </DropdownMenuItem>
              </DialogTrigger>
            )}

            {/* Midterms */}
            {/* 
                Buttons will only show if
                - current transaction is active
                AND
                - today is < policy expiration date
            */}
            {(showMidterms || enableERP) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Midterm Actions</DropdownMenuLabel>

                {/* 

                    Endorsement Midterm Button

                    Show if the policy is not cancelled

                */}
                {allowEndorsementMidterm && showMidterms && (
                  <Popover modal>
                    <PopoverTrigger asChild className="z-10">
                      <Button
                        className="flex w-full justify-start border-0 bg-none outline-none"
                        variant="ghost"
                      >
                        <span className="ml-2">Endorsement</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-8">
                      <div>
                        <EndorsementForm policy={policy} />
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {enableERP && <ERPForm policy={policy} />}

                {/*
                
                    Cancel Midterm Button

                    Show if the policy is not cancelled
                
                */}
                {allowCancelMidterm && showMidterms && (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => setCancelPolicyDialogOpen(true)}
                  >
                    <span className="ml-2">Cancel</span>
                  </Button>
                )}

                {/* 

                    Reinstate Midterm Button

                    Show if the policy is cancelled

                */}
                {allowReinstateMidterm &&
                  reinstateFeatureFlag &&
                  showMidterms && ( // update this to `true` when reinstate is ready
                    <Popover modal>
                      <PopoverTrigger asChild className="z-10">
                        <Button
                          className="flex w-full justify-start border-0 bg-none outline-none"
                          variant="ghost"
                        >
                          <span className="ml-2">Reinstate</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[500px] p-8">
                        <div>
                          <ReinstateForm policy={policy} />
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
              </>
            )}
            {role === userRoleEnum.SYSTEM_ADMIN &&
              policyBoundButTransactionInProgress && (
                <>
                  <DropdownMenuItem
                    onClick={() => setLockPolicyDialogOpen(true)}
                    disabled={loading}
                  >
                    <span className="ml-2 text-red-600">
                      {loading
                        ? 'Locking Policy'
                        : '***LOCK / FINALIZE POLICY***'}
                    </span>
                  </DropdownMenuItem>

                  {/* Button for re-open unbilled account */}
                  <DropdownMenuItem onClick={() => setReopenDialogOpen(true)}>
                    <span className="ml-2 text-orange-600">
                      Re-Open Unbilled Account
                    </span>
                  </DropdownMenuItem>
                </>
              )}
          </DropdownMenuContent>
        </DropdownMenu>
        <DialogContent
          className={`${dialogContent === 'RATING' ? ratingModalCSS : modalCSS}`}
        >
          {dialogContent === 'RATING' && (
            <DebugTransaction
              accountId={id!}
              setLoadingDebug={setLoadingDebug}
            />
          )}
          {dialogContent === 'TRANSFER' && (
            <Transfer accountId={id!} setFunction={setLoadingDebug} />
          )}
          {dialogContent === 'REFER' && (
            <Refer accountId={id!} setFunction={setLoadingDebug} />
          )}
          {dialogContent === 'DECLINE' && (
            <Decline
              title="Decline"
              accountId={id!}
              setFunction={setLoadingDebug}
            />
          )}

          {dialogContent === 'NOT_TAKEN' && (
            <Decline
              title="Not Taken"
              accountId={id!}
              setFunction={setLoadingDebug}
            />
          )}

          {dialogContent === 'DISCARD' && (
            <Discard accountId={id!} setFunction={setLoadingDebug} />
          )}

          {dialogContent === 'REOPEN' && (
            <Reopen accountId={id!} setFunction={setLoadingDebug} />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog to reopen account */}
      <CancelPolicyDialog
        isOpen={cancelPolicyDialogOpen}
        onOpenChange={setCancelPolicyDialogOpen}
        setMessage={setMessage}
        policy={policy}
      />
      {/* Confirmation dialog to reopen account */}
      <ReopenUnlockedAccountDialog
        isOpen={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        setMessage={setMessage}
        policyQK={policyQK}
        policyId={id!}
      />
      {/* Confirmation dialog to lock policy */}
      <LockPolicyDialog
        isOpen={lockPolicyDialogOpen}
        onOpenChange={setLockPolicyDialogOpen}
        isLoading={loading}
        setLoading={setLoading}
        setMessage={setMessage}
        policyId={id!}
      />

      {/* Confirmation dialog to issue policy */}
      <IssuePolicyDialog
        isOpen={issuePolicyDialogOpen}
        onOpenChange={setIssuePolicyDialogOpen}
        isIssuing={issuing}
        setIssuing={setIssuing}
        setMessage={setMessage}
        setLoading={setLoading}
        warehouseKey={warehouseKey}
        policyQK={policyQK}
        formulasQK={formulasQK}
        policyId={id!}
        permissionsQK={permissionsQK}
      />

      <BypassValidationDialog
        isOpen={bypassValidationDialogOpen}
        onOpenChange={setBypassValidationDialogOpen}
        isBypassing={bypassing}
        bypassed={policy?.bypassValidators}
        setBypassing={setBypassing}
        setMessage={setMessage}
        setLoading={setLoading}
        policyQK={policyQK}
        formulasQK={formulasQK}
        policyId={id!}
        permissionsQK={permissionsQK}
      />
    </>
  );
};
